import { useState, useMemo } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Row,
  Col,
  Card,
  CardBody,
  Badge,
  Input,
  Label,
  FormGroup,
} from "reactstrap";
import DataTable from "react-data-table-component";
import Select from "react-select";
import { Btn } from "../../../AbstractElements";
import { useDataFetch } from "../../Hooks/Use-data-fetch";
import { formatDate } from "../../utils/formatDate";

export const UserRankModal = ({ isOpen, toggle, rank }) => {
  const { getData } = useDataFetch();
  const [filters, setFilters] = useState({ code: "", country: "" });

  const { data: countries } = getData(["countries"], "/product/countries-available/all");
  const countryOptions = useMemo(
    () =>
      countries?.data?.countries?.map((c) => ({
        value: c.country_id,
        label: c.name,
      })) || [],
    [countries]
  );

  const filteredData = useMemo(() => {
    if (!rank?.users) return [];
    return rank.users
      .filter((u) => {
        if (filters.code && !u.user_id.multyhealth_code.toLowerCase().includes(filters.code.toLowerCase())) {
          return false;
        }
        if (filters.country && u.user_id.country_id !== filters.country) {
          return false;
        }
        return true;
      })
      .map((u) => ({
        ...u,
        userName: u.user_id.name,
        userCode: u.user_id.multyhealth_code,
        userEmail: u.user_id.email,
      }));
  }, [rank?.users, filters]);

  // Componente expandible para mostrar niveles
  const ExpandedComponent = ({ data }) => (
    <div className="p-3">
      <Card className="shadow-sm">
        <CardBody>
          <h6 className="mb-3">
            <i className="fa fa-layer-group me-2"></i>
            Desglose por Niveles
          </h6>
          {data.levels?.map((level, idx) => (
            <Row key={idx} className="mb-2 pb-2 border-bottom">
              <Col md="2">
                <Badge color="primary">Nivel {level.level}</Badge>
              </Col>
              <Col md="3">
                <strong>Puntos:</strong> {level.points?.toLocaleString() || 0}
              </Col>
              <Col md="3">
                <strong>Usuarios Red:</strong> {level.usersIds?.length || 0}
              </Col>
              <Col md="4">
                <strong>Órdenes:</strong> {level.orders?.length || 0}
              </Col>
            </Row>
          ))}
        </CardBody>
      </Card>
    </div>
  );

  const columns = [
    { name: "Código", selector: (row) => row.userCode, center: true, width: "120px" },
    { name: "Usuario", selector: (row) => row.userName, sortable: true },
    { name: "Email", selector: (row) => row.userEmail, sortable: true },
    { 
      name: "Total Puntos", 
      selector: (row) => row.total_points,
      format: (row) => row.total_points?.toLocaleString() || 0,
      right: true,
      width: "150px",
      sortable: true
    },
    {
      name: "Rango",
      selector: (row) => row.rank?.name || "Sin Rango",
      cell: (row) => (
        <div>
          <Badge color={row.rank?.qualifies ? "success" : "warning"}>
            {row.rank?.qualifies ? "✅" : "⚠️"} {row.rank?.name || "Sin Rango"}
          </Badge>
          {row.rank && !row.rank.qualifies && (
            <small className="text-muted d-block mt-1">
              Necesita {row.rank.required_active_users} activos (tiene {row.rank.active_level1_users})
            </small>
          )}
        </div>
      ),
      width: "200px",
    },
  ];

  if (!rank) return null;

  const totalPoints = rank.users?.reduce(
    (sum, user) => sum + (user.total_points || 0),
    0
  );

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl">
      <ModalHeader toggle={toggle} className="bg-primary text-white">
        <i className="fa fa-trophy me-2"></i> Detalle de Rangos de Usuarios
      </ModalHeader>
      <ModalBody>
        {/* 🔹 TOTALES ARRIBA */}
        <Row className="mb-4 g-2">
          <Col md="3" sm="6" xs="12">
            <Card className="shadow-sm p-2 text-center">
              <h6 className="mb-1 text-muted">Periodo</h6>
              <p className="mb-0 fw-bold">
                {rank.period?.start && formatDate(rank.period.start)}{" "}-{" "}
                {rank.period?.end && formatDate(rank.period.end)}
              </p>
            </Card>
          </Col>
          <Col md="3" sm="6" xs="12">
            <Card className="shadow-sm p-2 text-center">
              <h6 className="mb-1 text-muted">Fecha Cálculo</h6>
              <p className="mb-0 fw-bold">
                {rank.calculation_date
                  ? formatDate(rank.calculation_date)
                  : "—"}
              </p>
            </Card>
          </Col>
          <Col md="3" sm="6" xs="12">
            <Card className="shadow-sm p-2 text-center">
              <h6 className="mb-1 text-muted">Total Usuarios</h6>
              <p className="mb-0 fw-bold">{rank.users?.length || 0}</p>
            </Card>
          </Col>
          <Col md="3" sm="6" xs="12">
            <Card className="shadow-sm p-2 text-center">
              <h6 className="mb-1 text-muted">Total Puntos</h6>
              <p className="mb-0 fw-bold text-primary">
                {totalPoints?.toLocaleString() || 0}
              </p>
            </Card>
          </Col>
        </Row>

        {/* 🔹 FILTROS */}
        <Row className="mb-3 g-2">
          <Col md="3" sm="6" xs="12">
            <FormGroup>
              <Label className="form-label mb-1">Código</Label>
              <Input
                type="text"
                placeholder="Filtrar por código"
                value={filters.code}
                onChange={(e) => setFilters((prev) => ({ ...prev, code: e.target.value }))}
              />
            </FormGroup>
          </Col>
          <Col md="3" sm="6" xs="12">
            <FormGroup>
              <Label className="form-label mb-1">País</Label>
              <Select
                classNamePrefix="react-select"
                isClearable
                placeholder="Filtrar por país"
                options={countryOptions}
                value={
                  filters.country
                    ? {
                        value: filters.country,
                        label: countryOptions.find((o) => o.value === filters.country)?.label,
                      }
                    : null
                }
                onChange={(option) => setFilters((prev) => ({ ...prev, country: option ? option.value : "" }))}
              />
            </FormGroup>
          </Col>
        </Row>

        {/* 🔹 TABLA */}
        <DataTable
          columns={columns}
          data={filteredData}
          pagination
          highlightOnHover
          striped
          responsive
          noDataComponent="No hay información"
          expandableRows
          expandableRowsComponent={ExpandedComponent}
        />
      </ModalBody>

      <ModalFooter>
        <Btn attrBtn={{ color: "danger", className: "px-4", onClick: toggle }}>
          <i className="fa fa-times me-2"></i> Cerrar
        </Btn>
      </ModalFooter>
    </Modal>
  );
};
